import { createApi } from '@reduxjs/toolkit/query/react';
import { createAuthBaseQuery } from './baseQuery';

export const productionFlowApi = createApi({
  reducerPath: 'productionFlowApi',
  baseQuery: createAuthBaseQuery(),
  tagTypes: ['Pipeline', 'WorkOrder', 'Stats'],
  endpoints: (builder) => ({
    getPipeline: builder.query<Record<string, unknown[]>, void>({
      query: () => 'production-flow/pipeline',
      providesTags: ['Pipeline'],
    }),
    getProductionStats: builder.query<Record<string, number>, void>({
      query: () => 'production-flow/stats',
      providesTags: ['Stats'],
    }),
    createWorkOrderFromOrder: builder.mutation<unknown, { orderId: string; orderItemId?: string }>({
      query: ({ orderId, orderItemId }) => ({
        url: `production-flow/work-orders/from-order/${orderId}`,
        method: 'POST',
        body: { orderItemId },
      }),
      invalidatesTags: ['Pipeline', 'WorkOrder', 'Stats'],
    }),
    getWorkOrderDetail: builder.query<unknown, string>({
      query: (id) => `production-flow/work-orders/${id}`,
      providesTags: ['WorkOrder'],
    }),
    advanceStage: builder.mutation<unknown, string>({
      query: (id) => ({ url: `production-flow/work-orders/${id}/advance`, method: 'POST' }),
      invalidatesTags: ['Pipeline', 'WorkOrder', 'Stats'],
    }),
    approveDesign: builder.mutation<unknown, { id: string; userId: string }>({
      query: ({ id, userId }) => ({
        url: `production-flow/work-orders/${id}/approve-design`,
        method: 'POST',
        body: { userId },
      }),
      invalidatesTags: ['WorkOrder', 'Pipeline'],
    }),
    assignMachineToStage: builder.mutation<unknown, { stageRecordId: string; machineId: string }>({
      query: ({ stageRecordId, machineId }) => ({
        url: `production-flow/stages/${stageRecordId}/assign-machine`,
        method: 'POST',
        body: { machineId },
      }),
      invalidatesTags: ['WorkOrder', 'Pipeline'],
    }),
    completeStage: builder.mutation<unknown, { stageRecordId: string; data?: Record<string, unknown> }>({
      query: ({ stageRecordId, data }) => ({
        url: `production-flow/stages/${stageRecordId}/complete`,
        method: 'POST',
        body: data || {},
      }),
      invalidatesTags: ['WorkOrder', 'Pipeline', 'Stats'],
    }),
    scheduleDelivery: builder.mutation<unknown, { workOrderId: string; data: Record<string, unknown> }>({
      query: ({ workOrderId, data }) => ({
        url: `production-flow/work-orders/${workOrderId}/delivery`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['WorkOrder'],
    }),
    generateInvoice: builder.mutation<unknown, string>({
      query: (id) => ({ url: `production-flow/work-orders/${id}/invoice`, method: 'POST' }),
      invalidatesTags: ['WorkOrder'],
    }),
  }),
});

export const {
  useGetPipelineQuery,
  useGetProductionStatsQuery,
  useCreateWorkOrderFromOrderMutation,
  useGetWorkOrderDetailQuery,
  useAdvanceStageMutation,
  useApproveDesignMutation,
  useAssignMachineToStageMutation,
  useCompleteStageMutation,
  useScheduleDeliveryMutation,
  useGenerateInvoiceMutation,
} = productionFlowApi;
